import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { trackEvent, EVENTS } from "../lib/analytics";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Trash2, ChevronDown, ChevronRight, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import PremiumUpgradeModal from "../components/PremiumUpgradeModal";

const COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

export default function Syllabus() {
  const [subjects, setSubjects] = useState([]);
  const [chaptersBySub, setChaptersBySub] = useState({});
  const [openSubs, setOpenSubs] = useState({});
  const [newSub, setNewSub] = useState("");
  const [newColor, setNewColor] = useState(COLORS[0]);
  const [newChap, setNewChap] = useState({});
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // Inline subject-rename state. `renamingId` is the subject_id being edited
  // (null = none); `renameDraft` is the current input value. We commit on
  // submit/blur and bail on Escape — keeping the UX minimal and undo-friendly.
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/subjects");
    setSubjects(data);
    for (const s of data) {
      const { data: ch } = await api.get(`/chapters?subject_id=${s.subject_id}`);
      setChaptersBySub((prev) => ({ ...prev, [s.subject_id]: ch }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addSubject = async (e) => {
    e.preventDefault();
    if (!newSub.trim()) return;
    const { data: created } = await api.post("/subjects", { name: newSub.trim(), color: newColor });
    setSubjects((prev) => [...prev, created]);
    setChaptersBySub((prev) => ({ ...prev, [created.subject_id]: [] }));
    setNewSub("");
    // Cycle to next color so a quick "add three subjects in a row" gets variety
    setNewColor(COLORS[(COLORS.indexOf(newColor) + 1) % COLORS.length]);
    trackEvent(EVENTS.SUBJECT_ADDED, { subject_id: created.subject_id });
    toast.success("Subject added");
  };

  const removeSubject = async (id) => {
    await api.delete(`/subjects/${id}`);
    toast.success("Subject removed");
    load();
  };

  const startRename = (s) => {
    setRenamingId(s.subject_id);
    setRenameDraft(s.name);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameDraft("");
  };

  const commitRename = async () => {
    if (!renamingId) return;
    const trimmed = renameDraft.trim();
    const current = subjects.find((s) => s.subject_id === renamingId);
    if (!trimmed || trimmed === current?.name) {
      cancelRename();
      return;
    }
    try {
      const { data: updated } = await api.patch(`/subjects/${renamingId}`, { name: trimmed });
      setSubjects((prev) => prev.map((s) => (s.subject_id === renamingId ? { ...s, ...updated } : s)));
      toast.success("Subject renamed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rename. Try again.");
    } finally {
      cancelRename();
    }
  };

  const addChapter = async (subject_id) => {
    const name = (newChap[subject_id] || "").trim();
    if (!name) return;
    try {
      const { data: created } = await api.post("/chapters", { subject_id, name });
      setChaptersBySub((p) => ({ ...p, [subject_id]: [...(p[subject_id] || []), created] }));
      setSubjects((prev) => prev.map((s) => s.subject_id === subject_id
        ? { ...s, total_chapters: (s.total_chapters || 0) + 1,
            progress: (s.completed_chapters / ((s.total_chapters || 0) + 1)) * 100 }
        : s));
      setNewChap({ ...newChap, [subject_id]: "" });
      trackEvent(EVENTS.CHAPTER_ADDED, { subject_id, chapter_id: created.chapter_id });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && detail?.code === "free_chapter_limit") {
        // Soft conversion UX — open the upgrade modal instead of a toast alert.
        setUpgradeOpen(true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't add chapter");
      }
    }
  };

  const toggleChapter = async (chap) => {
    const updated = { ...chap, completed: !chap.completed };
    setChaptersBySub((p) => ({
      ...p,
      [chap.subject_id]: p[chap.subject_id].map((c) => c.chapter_id === chap.chapter_id ? updated : c),
    }));
    setSubjects((prev) => prev.map((s) => {
      if (s.subject_id !== chap.subject_id) return s;
      const done = s.completed_chapters + (updated.completed ? 1 : -1);
      return { ...s, completed_chapters: done, progress: s.total_chapters ? (done / s.total_chapters) * 100 : 0 };
    }));
    await api.patch(`/chapters/${chap.chapter_id}`, { completed: updated.completed });
  };

  const removeChapter = async (chap) => {
    await api.delete(`/chapters/${chap.chapter_id}`);
    setChaptersBySub((p) => ({
      ...p,
      [chap.subject_id]: p[chap.subject_id].filter((c) => c.chapter_id !== chap.chapter_id),
    }));
    load();
  };

  return (
    <div className="space-y-6" data-testid="syllabus-root">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Syllabus tracker</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Your syllabus, one tick at a time.</h1>
        <p className="text-zinc-600 dark:text-zinc-400">Add subjects, drop chapters, and watch progress roll in.</p>
      </div>

      <form onSubmit={addSubject} className="space-y-2.5 rounded-2xl border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900 sm:flex sm:items-center sm:gap-3 sm:space-y-0 sm:p-2.5" data-testid="add-subject-form">
        <Input
          placeholder="Add a subject (e.g., Physics, Biology)"
          value={newSub}
          onChange={(e)=>setNewSub(e.target.value)}
          data-testid="subject-name-input"
          className="border-0 bg-transparent shadow-none focus-visible:ring-0 sm:flex-1"
        />
        <div className="flex items-center gap-1.5" data-testid="subject-color-picker" role="radiogroup" aria-label="Subject color">
          {COLORS.map((c) => {
            const active = c === newColor;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setNewColor(c)}
                role="radio"
                aria-checked={active}
                aria-label={`Color ${c}`}
                data-testid={`subject-color-${c}`}
                className={`grid h-6 w-6 place-items-center rounded-full transition-transform ${active ? "ring-2 ring-offset-2 ring-offset-white dark:ring-offset-zinc-900 scale-110" : "hover:scale-110"}`}
                style={{ background: c, color: "#fff", boxShadow: active ? `0 0 0 1.5px ${c}` : undefined }}
              >
                {active && <span className="text-[10px] font-bold leading-none">✓</span>}
              </button>
            );
          })}
        </div>
        <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white sm:w-auto" data-testid="add-subject-btn">
          <Plus size={16} className="mr-1"/> Add subject
        </Button>
      </form>

      {subjects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 p-12 text-center dark:border-zinc-700">
          <p className="text-zinc-500">No subjects yet. Add your first subject to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {subjects.map((s) => {
            const chs = chaptersBySub[s.subject_id] || [];
            const open = openSubs[s.subject_id] ?? true;
            return (
              <div key={s.subject_id} className="rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900" data-testid={`subject-card-${s.subject_id}`}>
                <div className="flex items-center gap-3 p-4">
                  <button onClick={() => setOpenSubs({ ...openSubs, [s.subject_id]: !open })} className="grid h-7 w-7 place-items-center rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800" data-testid={`toggle-subject-${s.subject_id}`}>
                    {open ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                  </button>
                  <div className="h-8 w-1.5 rounded-full" style={{ background: s.color }} />
                  <div className="flex-1 min-w-0">
                    {renamingId === s.subject_id ? (
                      <form
                        onSubmit={(e) => { e.preventDefault(); commitRename(); }}
                        className="flex items-center gap-1.5"
                        data-testid={`rename-subject-form-${s.subject_id}`}
                      >
                        <Input
                          autoFocus
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Escape") cancelRename(); }}
                          maxLength={60}
                          className="h-8 flex-1 font-heading text-base font-semibold"
                          data-testid={`rename-subject-input-${s.subject_id}`}
                        />
                        <Button type="submit" size="icon" variant="ghost" aria-label="Save name" data-testid={`rename-subject-save-${s.subject_id}`}>
                          <Check size={16} className="text-emerald-600"/>
                        </Button>
                        <Button type="button" size="icon" variant="ghost" onClick={cancelRename} aria-label="Cancel rename" data-testid={`rename-subject-cancel-${s.subject_id}`}>
                          <X size={16} className="text-zinc-400"/>
                        </Button>
                      </form>
                    ) : (
                      <>
                        <div className="truncate font-heading text-base font-semibold" data-testid={`subject-name-${s.subject_id}`}>{s.name}</div>
                        <div className="text-xs text-zinc-500">{s.completed_chapters} / {s.total_chapters} chapters · {Math.round(s.progress)}%</div>
                      </>
                    )}
                  </div>
                  <div className="hidden w-48 sm:block">
                    <Progress value={s.progress} className="h-2" />
                  </div>
                  {renamingId !== s.subject_id && (
                    <Button size="icon" variant="ghost" onClick={() => startRename(s)} aria-label="Rename subject" data-testid={`edit-subject-${s.subject_id}`}>
                      <Pencil size={15} className="text-zinc-400 hover:text-emerald-600"/>
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => removeSubject(s.subject_id)} data-testid={`delete-subject-${s.subject_id}`}>
                    <Trash2 size={16} className="text-zinc-400 hover:text-red-500"/>
                  </Button>
                </div>

                {open && (
                  <div className="border-t border-zinc-100 px-4 py-3 dark:border-zinc-800">
                    <div className="space-y-1">
                      {chs.map((c) => (
                        <div key={c.chapter_id} className="group flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800/50" data-testid={`chapter-row-${c.chapter_id}`}>
                          <Checkbox
                            checked={c.completed}
                            onCheckedChange={() => toggleChapter(c)}
                            data-testid={`chapter-check-${c.chapter_id}`}
                          />
                          <span className={`flex-1 text-sm ${c.completed ? "line-through text-zinc-400" : ""}`}>{c.name}</span>
                          <button onClick={() => removeChapter(c)} className="opacity-0 group-hover:opacity-100" data-testid={`delete-chapter-${c.chapter_id}`}>
                            <Trash2 size={14} className="text-zinc-400 hover:text-red-500"/>
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex gap-2">
                      <Input
                        placeholder="Add a chapter…"
                        value={newChap[s.subject_id] || ""}
                        onChange={(e)=> setNewChap({...newChap, [s.subject_id]: e.target.value})}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChapter(s.subject_id); } }}
                        data-testid={`new-chapter-input-${s.subject_id}`}
                      />
                      <Button variant="outline" onClick={() => addChapter(s.subject_id)} data-testid={`add-chapter-btn-${s.subject_id}`}><Plus size={14}/></Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <PremiumUpgradeModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        title="You've reached your free chapter limit 🚀"
        description="Unlock unlimited chapters and premium features with Syllox Premium."
      />
    </div>
  );
}
